"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocationService = void 0;
const js_api_loader_1 = require("@googlemaps/js-api-loader");
const STORAGE_KEY = 'prospectGeoLocDetails';
// Global cache for client location and in-flight promise
let cachedGeoLocation = null;
let locationPromise = null;
class LocationService {
    constructor() { }
    static getInstance() {
        if (!this.instance) {
            this.instance = new LocationService();
        }
        return this.instance;
    }
    loadFromStorage() {
        try {
            if (globalThis.localStorage !== undefined) {
                const stored = globalThis.localStorage.getItem(STORAGE_KEY);
                if (stored) {
                    const parsed = JSON.parse(globalThis.atob(stored));
                    if (parsed?.user &&
                        typeof parsed.user === 'object' &&
                        typeof parsed.user.address === 'string' &&
                        typeof parsed.user.city?.long_name === 'string' &&
                        typeof parsed.user.country?.short_name === 'string' &&
                        typeof parsed.user.country?.long_name === 'string' &&
                        typeof parsed.user.postalCode?.long_name === 'string' &&
                        typeof parsed.user.state?.short_name === 'string' &&
                        typeof parsed.user.state?.long_name === 'string') {
                        return {
                            address: parsed.user.address || '',
                            city: parsed.user.city?.long_name || '',
                            country: {
                                abbr: parsed.user.country?.short_name || '',
                                name: parsed.user.country?.long_name || ''
                            },
                            postalCode: parsed.user.postalCode?.long_name || '',
                            stateProvince: {
                                abbr: parsed.user.state?.short_name || '',
                                name: parsed.user.state?.long_name || ''
                            }
                        };
                    }
                }
            }
        }
        catch {
            // Ignore storage or parsing errors
        }
        return null;
    }
    getPostalCodeFromStorage() {
        const stored = this.loadFromStorage();
        return stored?.postalCode || '';
    }
    saveToStorage(location) {
        // Do not store if all fields are empty
        const isEmpty = !location.address &&
            !location.city &&
            !location.country.abbr &&
            !location.country.name &&
            !location.postalCode &&
            !location.stateProvince.abbr &&
            !location.stateProvince.name;
        if (isEmpty)
            return;
        try {
            if (globalThis.localStorage !== undefined) {
                const data = {
                    user: {
                        address: location.address,
                        city: { short_name: location.city, long_name: location.city },
                        country: {
                            short_name: location.country.abbr,
                            long_name: location.country.name
                        },
                        postalCode: {
                            short_name: location.postalCode,
                            long_name: location.postalCode
                        },
                        state: {
                            short_name: location.stateProvince.abbr,
                            long_name: location.stateProvince.name
                        }
                    }
                };
                globalThis.localStorage.setItem(STORAGE_KEY, globalThis.btoa(JSON.stringify(data)));
            }
        }
        catch {
            // Ignore storage or serialization errors
        }
    }
    isGeolocationSupported() {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            return false;
        }
        return true;
    }
    /**
     * Returns the geolocation permission state: 'granted', 'denied', or 'unknown'.
     * Returns 'unknown' when the state is 'prompt' or cannot be determined,
     * since the Permissions API is unreliable for geolocation in Safari/Firefox.
     */
    async getGeolocationPermissionState() {
        try {
            if (navigator.permissions?.query) {
                const permissionStatus = await navigator.permissions.query({
                    name: 'geolocation'
                });
                if (permissionStatus && typeof permissionStatus.state === 'string') {
                    if (permissionStatus.state === 'granted' ||
                        permissionStatus.state === 'denied') {
                        return permissionStatus.state;
                    }
                    if (permissionStatus.state === 'prompt') {
                        // 'prompt' is unreliable (Safari/Firefox always return it),
                        // so fall through to 'unknown' and let the caller attempt
                        // geolocation directly, which triggers the real browser prompt.
                        return 'unknown';
                    }
                }
            }
        }
        catch {
            // ignore
        }
        return 'unknown';
    }
    getBrowserCoordinates() {
        return new Promise((resolve, reject) => {
            const onSuccess = (position) => {
                const coords = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                resolve(coords);
            };
            const onError = (error) => {
                reject(new Error(error.message));
            };
            navigator.geolocation.getCurrentPosition(onSuccess, onError, {
                enableHighAccuracy: true,
                timeout: 10000
            });
        });
    }
    async fetchAddressFromLatLong(lat, lng) {
        const key = window.GOOGLE_MAPS_CLIENT_KEY;
        if (!key) {
            throw new Error('Google Maps client key undefined');
        }
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            throw new TypeError('Invalid latitude or longitude');
        }
        try {
            (0, js_api_loader_1.setOptions)({ key, v: 'weekly' });
            const { Geocoder } = await (0, js_api_loader_1.importLibrary)('geocoding');
            const geocoder = new Geocoder();
            const response = await geocoder.geocode({ location: { lat, lng } });
            return { results: response.results };
        }
        catch {
            console.error('Error fetching geocode');
            throw new Error('Failed to fetch geocode');
        }
    }
    returnLocationFromAddressJson(json) {
        const components = json.results[0]?.address_components;
        if (!components?.length) {
            return {
                address: '',
                city: '',
                country: { abbr: '', name: '' },
                postalCode: '',
                stateProvince: { abbr: '', name: '' }
            };
        }
        const extract = (type, key = 'short_name') => components.find(c => c.types.includes(type))?.[key] ?? '';
        return {
            address: `${extract('street_number')} ${extract('route', 'long_name')}`.trim(),
            city: extract('locality'),
            postalCode: extract('postal_code'),
            stateProvince: {
                abbr: extract('administrative_area_level_1'),
                name: extract('administrative_area_level_1', 'long_name')
            },
            country: {
                abbr: extract('country'),
                name: extract('country', 'long_name')
            }
        };
    }
    async get() {
        if (!window.GOOGLE_MAPS_CLIENT_KEY) {
            return { status: 'no-location' };
        }
        if (cachedGeoLocation) {
            return { status: 'success', location: cachedGeoLocation };
        }
        if (locationPromise) {
            return locationPromise;
        }
        const stored = this.loadFromStorage();
        if (stored) {
            cachedGeoLocation = stored;
            return { status: 'success', location: stored };
        }
        locationPromise = (async () => {
            if (!this.isGeolocationSupported()) {
                console.error('Geolocation not supported by browser');
                locationPromise = null;
                return {
                    status: 'error',
                    error: 'Failed to get location'
                };
            }
            const permissionState = await this.getGeolocationPermissionState();
            if (permissionState === 'denied') {
                locationPromise = null;
                return {
                    status: 'error',
                    error: 'Geolocation permission denied'
                };
            }
            // permissionState === 'granted' or 'unknown' (fallback)
            try {
                const coords = await this.getBrowserCoordinates();
                const addressJson = await this.fetchAddressFromLatLong(coords.lat, coords.lng);
                const location = this.returnLocationFromAddressJson(addressJson);
                cachedGeoLocation = location;
                this.saveToStorage(location);
                locationPromise = null;
                return { status: 'success', location };
            }
            catch {
                console.error('Failed to get location');
                locationPromise = null;
                return {
                    status: 'error',
                    error: 'Failed to get browser coordinates'
                };
            }
        })();
        return locationPromise;
    }
    async getPostalCode() {
        const result = await this.get();
        if (result.status === 'success') {
            return result.location.postalCode || '';
        }
        return '';
    }
    static clearCache() {
        cachedGeoLocation = null;
        locationPromise = null;
        try {
            globalThis.localStorage?.removeItem(STORAGE_KEY);
        }
        catch {
            // Ignore storage errors
        }
    }
}
exports.LocationService = LocationService;
